import { parseClaimBlockingItem, type ClaimBlockingItemDto, type ClaimBlockingKind } from '@his/shared';

export type ClaimBlockKey =
  | 'nx_claim_block_not_billable'
  | 'nx_claim_block_unpaid'
  | 'nx_claim_block_encounter_open'
  | 'nx_claim_block_admission_open'
  | 'nx_claim_block_nothing_approved'
  | 'nx_claim_block_already_claimed';

export interface ClaimBlock {
  key: ClaimBlockKey;
  dueMinor: number;
  number: string | null;
}

export interface ClaimGateInput {
  status: string;
  number: string;
  netMinor: number;
  creditMinor: number;
  paidMinor: number;
  everClaimed: boolean;
  hasApproval: boolean;
  encounterId: string | null;
  admissionId: string | null;
  encounterStatus: string | null | undefined;
  admissionStatus: string | null | undefined;
}

export function patientDueMinor(input: Pick<ClaimGateInput, 'netMinor' | 'creditMinor' | 'paidMinor'>): number {
  return Math.max(0, input.netMinor - input.creditMinor - input.paidMinor);
}

export function claimBlockOf(input: ClaimGateInput): ClaimBlock | null {
  const base = { dueMinor: 0, number: input.number };
  if (input.everClaimed) return { ...base, key: 'nx_claim_block_already_claimed' };
  if (['draft', 'cancelled', 'refunded'].includes(input.status)) return { ...base, key: 'nx_claim_block_not_billable' };
  const due = patientDueMinor(input);
  if (due > 0) return { ...base, key: 'nx_claim_block_unpaid', dueMinor: due };
  if (input.admissionId) {
    if (input.admissionStatus === 'admitted') return { ...base, key: 'nx_claim_block_admission_open' };
  } else if (input.encounterId && input.encounterStatus && input.encounterStatus !== 'closed') {
    return { ...base, key: 'nx_claim_block_encounter_open' };
  }
  if (!input.hasApproval) return { ...base, key: 'nx_claim_block_nothing_approved' };
  return null;
}

export type ClaimChip = 'created' | 'auto_pending';

const WAITING_KEYS: readonly ClaimBlockKey[] = ['nx_claim_block_unpaid', 'nx_claim_block_encounter_open', 'nx_claim_block_admission_open'];

export function claimChipOf(block: ClaimBlock | null, hasApproval: boolean): ClaimChip | null {
  if (block?.key === 'nx_claim_block_already_claimed') return 'created';
  if (block && hasApproval && WAITING_KEYS.includes(block.key)) return 'auto_pending';
  return null;
}

const KEY_OF_KIND: Readonly<Record<ClaimBlockingKind, ClaimBlockKey>> = {
  invoice_unpaid: 'nx_claim_block_unpaid',
  encounter_open: 'nx_claim_block_encounter_open',
  admission_open: 'nx_claim_block_admission_open',
  nothing_approved: 'nx_claim_block_nothing_approved',
};

export function claimBlockOfItem(item: ClaimBlockingItemDto): ClaimBlock {
  return { key: KEY_OF_KIND[item.kind] ?? 'nx_claim_block_not_billable', dueMinor: item.dueMinor, number: item.invoiceNumber };
}

export function claimBlockOfError(error: unknown): ClaimBlock | null {
  const details = (error as { details?: { path?: string; message: string }[] } | null)?.details;
  const item = parseClaimBlockingItem(details);
  return item ? claimBlockOfItem(item) : null;
}
